import { ErpatApiError } from '@erpat/sdk';
import {
  erpatClient,
  accessTokenExpiringWithin,
  adoptPersistedTokens,
  SDK_ACCESS_TOKEN_KEY,
  SDK_REFRESH_TOKEN_KEY,
} from '@/services/api/erpat-client';
import { refreshSession, handleSessionExpired, isForeignResponse } from '@/services/api/http';
import { isSecurityChallenge } from '@/utils/edge-challenge';

/**
 * Proactive background token refresher + cross-tab token sync.
 *
 * The reactive path in `http.ts` (`withAuthRetry`) already recovers from an
 * expired access token by refreshing on a 401 and retrying. This module adds a
 * *proactive* refresh so the access token is rotated **before** it expires,
 * which avoids the first-request-after-idle stall and keeps long-lived screens
 * (kept-alive Ionic tabs) from ever holding a dead token — a constantly-used
 * app never hits a session timeout.
 *
 * Metronome: the tick comes from a dedicated Web Worker
 * (`refresh-metronome.worker.ts`) rather than a main-thread interval, because
 * Chrome throttles main-thread timers in background tabs while dedicated-worker
 * timers are exempt — so a backgrounded/minimized window keeps ticking on
 * schedule. Where a worker can't be constructed (ancient webview, worker-src
 * CSP), a plain `setInterval` fallback takes over. A frozen/discarded tab runs
 * NOTHING (not even workers); those gaps are bridged by the wake-up listeners
 * (`visibilitychange`, Page Lifecycle `resume`, bfcache `pageshow`, `online`),
 * each triggering an immediate gated check, and by the long-lived refresh
 * token itself. Deliberately NOT the service worker: browsers terminate idle
 * SWs in seconds, and SW-side rotation would split token custody away from the
 * page's `localStorage` — see docs/background-token-refresh-plan.md.
 *
 * Cadence: the token is *checked* every {@link REFRESH_INTERVAL_MS} (and on
 * every wake-up event) but only *rotated* when it would not survive to the
 * next check. Rotation is deliberately rationed: the server consumes a
 * refresh token on first use and revokes the WHOLE session family when a
 * consumed token is replayed, so every needless rotation is another chance for
 * a lost response or a second tab to trip that reuse detection and force a
 * sign-out. The refresh itself is single-flighted per tab and serialized
 * across tabs in `http.ts`.
 *
 * Cross-tab sync (web/PWA): the SDK reads its persisted tokens only at
 * construction, so a `storage` listener adopts a pair rotated by another tab
 * (keeping this tab's in-memory copy current) and tears the session down when
 * another tab signs out — instead of letting this tab replay a dead token.
 *
 * Lifecycle is driven by the session store: started when a token pair is issued
 * or restored, stopped on sign-out / session expiry.
 */

/**
 * How often the token's remaining lifetime is checked while a session is
 * active. Posted to the metronome worker on start so both sides share one
 * cadence (the worker's own default is a twin of this value).
 */
const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Rotate when the access token expires within this window — i.e. it would not
 * survive to the next scheduled check (plus a minute of margin for timer
 * throttling in background tabs). With the server's 15-minute access TTL this
 * yields one rotation per lifetime instead of rotating on every tick.
 */
const NEAR_EXPIRY_WINDOW_SEC = REFRESH_INTERVAL_MS / 1000 + 60;

let timer: ReturnType<typeof setInterval> | null = null;
let worker: Worker | null = null;

/** Attempt one near-expiry-gated refresh. */
async function refreshTick(): Promise<void> {
  // Catch up on a rotation another tab performed (e.g. events missed while the
  // OS had this webview frozen) BEFORE judging expiry or presenting a token.
  adoptPersistedTokens();
  // Can't refresh without a refresh token (signed out, or never authenticated).
  if (!erpatClient.getRefreshToken()) return;
  if (!accessTokenExpiringWithin(NEAR_EXPIRY_WINDOW_SEC)) return;

  try {
    await refreshSession();
  } catch (err) {
    // Only a 401/403 carrying ERPat's own envelope is a verdict on the refresh
    // token → the session is unrecoverable; tear it down (this also stops the
    // timer via the store's sign-out). A 401/403 that does not carry ERPat's
    // envelope — a web page or a bare status: an edge's block response, a
    // captive portal, a WAF — never forwarded the token, so it says nothing
    // about it; and a challenge carrying the edge's marker already arrives here
    // as status 0. Signing out on either would end a session the server never
    // refused. Those, like every transient failure (network, 5xx, status 0),
    // are swallowed — the next tick, or a real request's 401 retry, will
    // recover.
    if (
      err instanceof ErpatApiError &&
      (err.status === 401 || err.status === 403) &&
      !isForeignResponse(err) &&
      !isSecurityChallenge(err)
    ) {
      handleSessionExpired();
    }
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === 'visible') void refreshTick();
}

/**
 * Page Lifecycle `resume` — fires when the browser unfreezes a frozen tab
 * (which can happen without the tab becoming visible, so `visibilitychange`
 * alone misses it). While frozen, NOTHING ran — not even the worker — so check
 * immediately. A no-op on browsers that never freeze/fire it.
 */
function onResume(): void {
  void refreshTick();
}

/**
 * bfcache restore — a `persisted` pageshow skips normal startup entirely, so
 * the restored page may hold an hours-old in-memory token pair.
 */
function onPageShow(event: PageTransitionEvent): void {
  if (event.persisted) void refreshTick();
}

/**
 * Network returned — recover promptly instead of waiting up to a full
 * interval (ticks during an outage swallow their transport errors by design).
 */
function onOnline(): void {
  void refreshTick();
}

/**
 * Start the tick source. Primary: a dedicated Web Worker, whose timers escape
 * background-tab throttling. Fallback: a main-thread interval, when the worker
 * can't be constructed or later fails to load (CSP, packaging).
 */
function startMetronome(): void {
  try {
    worker = new Worker(new URL('./refresh-metronome.worker.ts', import.meta.url), {
      type: 'module',
    });
    worker.onmessage = () => void refreshTick();
    worker.onerror = () => {
      // Script failed to load/run — swap to the interval fallback. Fires only
      // while we own a live worker (terminate() silences it), so this cannot
      // revive a stopped metronome.
      stopMetronome();
      timer = setInterval(() => void refreshTick(), REFRESH_INTERVAL_MS);
    };
    // Retune the worker to OUR cadence so the two constants can never drift.
    worker.postMessage({ intervalMs: REFRESH_INTERVAL_MS });
  } catch {
    worker = null;
  }
  if (!worker) {
    timer = setInterval(() => void refreshTick(), REFRESH_INTERVAL_MS);
  }
}

/** Stop whichever tick source is active. Idempotent. */
function stopMetronome(): void {
  if (worker !== null) {
    worker.terminate();
    worker = null;
  }
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
}

/**
 * React to another tab changing the persisted token pair. Rotation → adopt the
 * new pair; removal (sign-out / expiry in that tab) → tear this session down
 * too, so this tab never replays a token whose family the server has closed.
 */
function onStorageEvent(event: StorageEvent): void {
  // `key === null` means storage.clear() — the token keys are gone with it.
  const tokenKey =
    event.key === null || event.key === SDK_ACCESS_TOKEN_KEY || event.key === SDK_REFRESH_TOKEN_KEY;
  if (!tokenKey) return;
  let stored: string | null = null;
  try {
    stored = localStorage.getItem(SDK_ACCESS_TOKEN_KEY);
  } catch {
    return;
  }
  if (stored === null) {
    handleSessionExpired();
    return;
  }
  adoptPersistedTokens();
}

/**
 * Start the background refresher. Idempotent — calling it while already running
 * is a no-op. Runs an immediate near-expiry check (covers a session restored
 * after the app was closed past the token's lifetime), then checks on the
 * worker-driven interval and on every wake-up path (foreground, unfreeze,
 * bfcache restore, network return), and keeps this tab's tokens in sync with
 * rotations / sign-outs performed by other tabs.
 */
export function startTokenAutoRefresh(): void {
  if (timer !== null || worker !== null) return;
  startMetronome();
  document.addEventListener('visibilitychange', onVisibilityChange);
  // Page Lifecycle 'resume' is not in TS's DocumentEventMap yet — the string
  // overload of addEventListener covers it; unsupported browsers never fire it.
  document.addEventListener('resume', onResume);
  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('online', onOnline);
  window.addEventListener('storage', onStorageEvent);
  void refreshTick();
}

/** Stop the background refresher and detach listeners. Idempotent. */
export function stopTokenAutoRefresh(): void {
  stopMetronome();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  document.removeEventListener('resume', onResume);
  window.removeEventListener('pageshow', onPageShow);
  window.removeEventListener('online', onOnline);
  window.removeEventListener('storage', onStorageEvent);
}
